import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Link,
  useTheme,
} from '@mui/material';
import {
  Assignment,
  Help,
  Loop,
  Security,
  Person,
  Edit,
  Phone,
} from '@mui/icons-material';

export default function PrivacyPolicy() {
  const theme = useTheme();

  const sections = [
    {
      id: '1',
      title: 'Information We Collect',
      icon: <Assignment color="primary" />,
      content: (
        <>
          <Typography variant="body1" paragraph>
            We collect the following information from both students and instructors upon registration and use of the platform:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Full Name" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Email Address" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Phone Number" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Password (securely encrypted)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Location Details" />
            </ListItem>
          </List>
        </>
      )
    },
    {
      id: '2',
      title: 'Why We Collect This Information',
      icon: <Help color="primary" />,
      content: (
        <List dense>
          <ListItem>
            <ListItemText primary="To create and manage user accounts" />
          </ListItem>
          <ListItem>
            <ListItemText primary="To facilitate communication and access to classroom activities" />
          </ListItem>
          <ListItem>
            <ListItemText primary="To personalize user experiences and improve our services" />
          </ListItem>
          <ListItem>
            <ListItemText primary="To prepare for future scaling by allowing database partitioning based on location" />
          </ListItem>
          <ListItem>
            <ListItemText primary="To enforce platform policies and ensure a safe learning environment" />
          </ListItem>
        </List>
      )
    },
    {
      id: '3',
      title: 'How We Use Your Information',
      icon: <Loop color="primary" />,
      content: (
        <Typography variant="body1" paragraph>
          Your data is used strictly for educational and administrative purposes within the ELMS platform. 
          We do not sell, rent, or share your personal information with third parties for marketing purposes.
        </Typography>
      )
    },
    {
      id: '4',
      title: 'Data Security',
      icon: <Security color="primary" />,
      content: (
        <Typography variant="body1" paragraph>
          We implement industry-standard security measures to protect your information. Passwords are 
          encrypted and sensitive data is safeguarded to prevent unauthorized access.
        </Typography>
      )
    },
    {
      id: '5',
      title: 'User Control',
      icon: <Person color="primary" />,
      content: (
        <>
          <Typography variant="body1" paragraph>
            Users have the right to:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Access and update their personal information" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Request account deletion" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Contact us regarding any concerns related to their data" />
            </ListItem>
          </List>
        </>
      )
    },
    {
      id: '6',
      title: 'Policy Updates',
      icon: <Edit color="primary" />,
      content: (
        <Typography variant="body1" paragraph>
          This policy may be updated from time to time. Users will be notified via email or platform 
          notification in the event of significant changes.
        </Typography>
      )
    },
    {
      id: '7',
      title: 'Contact',
      icon: <Phone color="primary" />,
      content: (
        <>
          <Typography variant="body1" paragraph>
            If you have any questions or concerns about this Privacy Policy, please contact:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">📧</Typography>
              <Typography variant="body2">+2347059148543</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">🌐</Typography>
              <Link href="https://elms.ng" target="_blank" rel="noopener noreferrer">
                https://elms.ng
              </Link>
            </Box>
          </Box>
        </>
      )
    }
  ];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            ELMS Privacy Policy
          </Typography>
          <Chip 
            label="Effective Date: 24th April 2025" 
            color="primary" 
            variant="outlined"
            sx={{ fontSize: '0.9rem', px: 2 }}
          />
        </Box>

        {/* Introduction */}
        <Box sx={{ mb: 4, p: 3, backgroundColor: theme.palette.grey[50], borderRadius: 2 }}>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
            At ELMS (E-Learning Management System), we value your privacy and are committed to protecting your personal information. 
            This Privacy Policy explains what data we collect, why we collect it, and how we use and protect it.
          </Typography>
        </Box>

        {/* Privacy Sections */}
        {sections.map((section, index) => (
          <Box key={section.id} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {section.icon}
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {section.id}. {section.title}
              </Typography>
            </Box>
            
            <Box sx={{ ml: 5 }}>
              {section.content}
            </Box>
            
            {index < sections.length - 1 && (
              <Divider sx={{ mt: 3, opacity: 0.3 }} />
            )}
          </Box>
        ))}

        {/* Footer */}
        <Box sx={{ mt: 6, pt: 3, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Your privacy is important to us. By using ELMS, you consent to the practices described in this Privacy Policy.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}